import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import "./home-page.css";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Header, MobileMenu, MobileNav, Sidebar } from "@/components/layout-components";
import { BestSellers, CartContents, CategoriesSection, Hero, ProductsSection } from "@/components/app-components";
import { Drawer } from "@/components/ui-components/drawer";
import { Toast } from "@/components/ui-components/toast";
import { calculateProductTotal, type Product } from "@/data/products";
import { applyDocumentLocale, normalizeLocale } from "@/i18n";
import { type InterpolationValues } from "@/i18n/types";
import { catalogApi } from "@/services/catalog/catalog-api";
import { useUIStore } from "@/app/state/ui-store";
import { useCartStore } from "@/app/state/cart-store";
import { useAuthStore } from "@/app/state/auth-store";
import { productDetailsPath } from "@/utils/product-url";

interface ToastState { id: string; nonce: number; }

/**
 * Storefront page composition root. It owns page-level queries and interaction
 * coordination, then passes data and commands into reusable feature sections.
 */
export function HomePage() {
    const { t: translate, i18n } = useTranslation();
    const locale = normalizeLocale(i18n.language);
    const t = (key: string, values?: InterpolationValues) => translate(key, values as Record<string, unknown>) as string;
    const [search, setSearch] = useState("");
    const [toast, setToast] = useState<ToastState | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    // Prices (and possibly other fields) are audience-dependent server-side
    // (guests get `null` prices, authenticated users get real ones), so
    // auth status is part of the catalog query keys below - switching users
    // or signing out triggers a fresh fetch instead of showing stale data.
    const authStatus = useAuthStore(state => state.status);
    const sessionVersion = useAuthStore(state => state.sessionVersion);

    const categoriesQuery = useQuery({
        queryKey: ["catalog", "categories", locale, authStatus, sessionVersion],
        queryFn: () => catalogApi.listCategories(locale)
    });
    const categoryData = categoriesQuery.data ?? [];

    // Product listing requires a categoryId server-side (`GetProducts` route
    // has no "all categories" mode), so pick the first category that
    // actually has products once real categories are known.
    const activeCategory = useMemo(() => categoryData.find(category => category.count > 0) ?? categoryData[0] ?? null, [categoryData]);
    const usingRealCategory = Boolean(activeCategory?.id);

    const productsQuery = useQuery({
        queryKey: ["catalog", "products", locale, activeCategory?.id, authStatus, sessionVersion],
        queryFn: () => catalogApi.listProducts(locale, activeCategory!.id),
        enabled: usingRealCategory
    });
    const productData = productsQuery.data?.products ?? [];

    // Debounced live product search against the real catalog API (spans the
    // whole catalog, unlike `productData` which is scoped to one category).
    // Falls back to filtering the currently-loaded category/mock products
    // locally so the search box still works while the debounce is pending
    // or if the search request fails.
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
        return () => window.clearTimeout(timer);
    }, [search]);
    const searchQuery = useQuery({
        queryKey: ["catalog", "search", debouncedSearch, authStatus, sessionVersion],
        queryFn: () => catalogApi.searchProducts(debouncedSearch),
        enabled: usingRealCategory && debouncedSearch.length > 0
    });
    const searchResults = debouncedSearch
        ? (searchQuery.data ?? productData.filter(product => `${product.name} ${product.description}`.toLocaleLowerCase(locale).includes(debouncedSearch.toLocaleLowerCase(locale))))
        : [];

    // Real top-sellers come from `product_popularity` (synced from Positive
    // sales reports); falls back to the mock `bestSellerRank`-sorted list
    // until it resolves or if there's no popularity data yet.
    const topProductsQuery = useQuery({
        queryKey: ["catalog", "top-products", locale, authStatus, sessionVersion],
        queryFn: () => catalogApi.listTopProducts(locale, 4)
    });
    const bestSellersData = topProductsQuery.data ?? [];

    const appearance = useUIStore(state => state.appearance);
    const menuOpen = useUIStore(state => state.menuOpen);
    const cartOpen = useUIStore(state => state.cartOpen);
    const setMenuOpen = useUIStore(state => state.setMenuOpen);
    const setCartOpen = useUIStore(state => state.setCartOpen);
    const toggleAppearance = useUIStore(state => state.toggleAppearance);

    const quantities = useCartStore(state => state.quantities);
    const cart = useCartStore(state => state.cart);
    const cartItems = useCartStore(state => state.cartItems);
    const cartSummary = useCartStore(state => state.summary);
    const pendingProductIds = useCartStore(state => state.pendingProductIds);
    const cartError = useCartStore(state => state.error);
    const setQuantity = useCartStore(state => state.setQuantity);
    const setCartQuantity = useCartStore(state => state.setCartQuantity);
    const addToCart = useCartStore(state => state.addToCart);
    const removeFromCart = useCartStore(state => state.removeFromCart);

    const closeMenu = useCallback(() => setMenuOpen(false), [setMenuOpen]);
    const closeCart = useCallback(() => setCartOpen(false), [setCartOpen]);
    useLayoutEffect(() => {
        let frame = 0;
        let observeFrame = 0;
        const syncRailHeight = () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(() => {
                const rail = document.querySelector<HTMLElement>('.rail');
                if (!rail) {
                    syncRailHeight();
                    return;
                }
                const rootStyles = getComputedStyle(document.documentElement);
                const rootSize = Number.parseFloat(rootStyles.fontSize) || 16;
                const bottomGap = rootSize * 0.625;
                const stickyOffsetValue = rootStyles.getPropertyValue("--site-sticky-offset").trim();
                const stickyOffset = Number.parseFloat(stickyOffsetValue) || rootSize * 7;
                const stickyOffsetInPixels = stickyOffsetValue.endsWith("rem") ? stickyOffset * rootSize : stickyOffset;
                const height = Math.max(0, window.innerHeight - stickyOffsetInPixels - bottomGap);
                document.documentElement.style.setProperty('--site-rail-height', `${height}px`);
            });
        };
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(syncRailHeight);
        const observeRail = () => {
            const rail = document.querySelector<HTMLElement>('.rail');
            if (!rail) {
                observeFrame = window.requestAnimationFrame(observeRail);
                return;
            }
            observer?.observe(rail);
            syncRailHeight();
        };
        observeRail();
        window.addEventListener('resize', syncRailHeight);
        window.addEventListener('scroll', syncRailHeight, { passive: true });
        return () => {
            window.cancelAnimationFrame(frame);
            window.cancelAnimationFrame(observeFrame);
            observer?.disconnect();
            window.removeEventListener('resize', syncRailHeight);
            window.removeEventListener('scroll', syncRailHeight);
            document.documentElement.style.removeProperty('--site-rail-height');
        };
    }, []);
    useEffect(() => {
        document.body.classList.toggle("locked", menuOpen || cartOpen);
        return () => document.body.classList.remove("locked");
    }, [menuOpen, cartOpen]);

    useEffect(() => {
        if (!toast) return;
        const timer = window.setTimeout(() => setToast(null), 2200);
        return () => window.clearTimeout(timer);
    }, [toast]);

    const changeLocale = (code: typeof locale) => {
        const parts = location.pathname.split("/").filter(Boolean);
        if (parts.length && parts[0] in { en: true, he: true, th: true }) parts[0] = code;
        else parts.unshift(code);
        void i18n.changeLanguage(code);
        applyDocumentLocale(code);
        navigate(`/${parts.join("/")}${location.search}${location.hash}`, { replace: true });
    };

    const cartEntries = useMemo(() => Object.entries(cart), [cart]);
    const cartCount = useMemo(() => cartEntries.reduce((sum, [, quantity]) => sum + quantity, 0), [cartEntries]);
    const localCartTotal = useMemo(() => cartEntries.reduce((sum, [id, quantity]) => { const product = productData.find(item => item.id === id); return sum + (product ? calculateProductTotal(product, quantity).total : 0); }, 0), [cartEntries, productData]);
    const cartTotal = cartSummary?.subtotal ?? localCartTotal;

    const openProduct = useCallback((product: Product | string) => {
        setSearch("");
        setMenuOpen(false);
        setCartOpen(false);
        navigate(productDetailsPath(locale, product));
    }, [locale, navigate, setCartOpen, setMenuOpen]);

    const handleAddToCart = useCallback((id: string) => {
        void addToCart(id, locale).then(updated => {
            if (updated) setToast({ id, nonce: Date.now() });
        });
    }, [addToCart, locale]);

    const scrollTo = useCallback((target: "top" | "categories" | "products") => {
        closeMenu();
        window.setTimeout(() => {
            if (target === "top") window.scrollTo({ top: 0, behavior: "smooth" });
            else document.getElementById(target)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 20);
    }, [closeMenu]);

    const focusSearch = useCallback(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        window.setTimeout(() => document.getElementById("storefront-search-input")?.focus(), 220);
    }, []);

    useEffect(() => {
        if (!(location.state as { focusSearch?: boolean } | null)?.focusSearch) return;
        focusSearch();
        navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null });
    }, [focusSearch, location.hash, location.pathname, location.search, location.state, navigate]);
    return (
        <div className="react-app-root">
            <Header
                locale={locale}
                appearance={appearance}
                menuOpen={menuOpen}
                cartOpen={cartOpen}
                cartCount={cartCount}
                cartTotal={cartTotal}
                search={search}
                searchResults={searchResults}
                t={t}
                onLocaleChange={changeLocale}
                onAppearanceToggle={toggleAppearance}
                onSearchChange={setSearch}
                onProductSelect={openProduct}
                onMenuOpen={() => setMenuOpen(true)}
                onCartOpen={() => setCartOpen(true)}
                categories={categoryData}
            />

            <main className="app shell">
                <div className="app-grid">
                    <Sidebar categories={categoryData} locale={locale} t={t}>
                    </Sidebar>
                    <section className="workspace">
                        <Hero t={t} />
                        <CategoriesSection categories={categoryData} locale={locale} t={t} />
                        <ProductsSection products={productData} categories={categoryData} locale={locale} quantities={quantities} t={t} onQuantityChange={setQuantity} onAdd={handleAddToCart} />
                    </section>
                    <BestSellers products={bestSellersData} locale={locale} t={t} onAdd={handleAddToCart} />
                </div>
            </main>

            <Drawer open={menuOpen} title={t("layout.sidebar.title")} closeLabel={t("common.accessibility.close")} panelId="shop-menu-drawer" onClose={closeMenu}>
                <MobileMenu categories={categoryData} locale={locale} t={t} onNavigate={closeMenu} />
            </Drawer>

            <Drawer open={cartOpen} title={t("commerce.cart.title")} closeLabel={t("common.accessibility.close")} panelId="cart-drawer" side="end" onClose={closeCart}>
                <CartContents
                    products={productData}
                    cart={cart}
                    cartItems={cartItems}
                    pendingProductIds={pendingProductIds}
                    error={cartError}
                    locale={locale}
                    subtotal={cartTotal}
                    t={t}
                    onOpenProduct={openProduct}
                    onQuantityChange={(id, quantity) => setCartQuantity(id, quantity, locale)}
                    onRemove={id => removeFromCart(id, locale)}
                />
            </Drawer>

            {toast ? <Toast show title={t("commerce.cart.addedTitle")} message={t("commerce.cart.addedMessage")} actionLabel={t("commerce.cart.viewCart")} onAction={() => { setToast(null); setCartOpen(true); }} /> : null}

            <MobileNav cartCount={cartCount} t={t} activeItem="home" onHome={() => scrollTo("top")} onCategories={() => navigate(`/${locale}/categories`)} onSearch={focusSearch} onMenu={() => setMenuOpen(true)} onCart={() => setCartOpen(true)} />
        </div>
    );
}
